import { last, sortedIndexOf } from 'lodash';
import { BlocksNode, NodeLayoutData } from '../types';
import { isGroup } from '../util';
import { isOdd } from 'tabview/visualizations/misc/math';

/* strip unused rows and columns from the specified node and its descendency */
export const stripLayout = (rootNode: BlocksNode): void => {
  const setLayout = (
    u: BlocksNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => {
    u.layoutData = { col, row, colSpan, rowSpan };
  };

  const doit = (node: BlocksNode) => {
    if (node.children) {
      for (const child of node.children) {
        stripLayout(child);
      }

      stripGrid(node, setLayout);
    }
  };

  doit(rootNode);
};

export const setNodeLayoutData = (
  node: BlocksNode,
  nodeLayoutData: NodeLayoutData | undefined
): boolean => {
  if (nodeLayoutData && isValidLayoutData(nodeLayoutData)) {
    node.layoutData = {
      col: nodeLayoutData.col,
      colSpan: nodeLayoutData.colSpan,
      row: nodeLayoutData.row,
      rowSpan: nodeLayoutData.rowSpan,
    };

    node.open =
      isGroup(node) && nodeLayoutData.open === undefined
        ? true
        : nodeLayoutData.open;

    return true;
  }

  node.layoutData = undefined;
  node.open = true;

  return false;
};

export const getNodeLayoutData = (
  u: BlocksNode
): NodeLayoutData | undefined => {
  return u.layoutData;
};

export const isValidLayoutData = (nodeLayoutData: NodeLayoutData): boolean => {
  if (!isFinite(nodeLayoutData.col) || nodeLayoutData.col < 0) {
    return false;
  }

  if (!isFinite(nodeLayoutData.colSpan) || nodeLayoutData.colSpan < 1) {
    return false;
  }

  if (!isFinite(nodeLayoutData.row) || nodeLayoutData.row < 0) {
    return false;
  }

  if (!isFinite(nodeLayoutData.rowSpan) || nodeLayoutData.rowSpan < 1) {
    return false;
  }

  return true;
};

export const buildOrdinates = <Type>(
  nodes: Iterable<Type>,
  minEdge: (node: Type) => number,
  maxEdge: (node: Type) => number
): number[] => {
  const epsilon = 0.01;

  /* initialise event queue and set of ordinates */

  const events = [];
  const ordinates: number[] = [];

  for (const node of nodes) {
    events.push({ isEnter: true, position: minEdge(node) });
    events.push({ isEnter: false, position: maxEdge(node) });
  }

  // lexical sort by event position and edge (exit is before enter)
  // this sort is backwards to that I can conveniently process events
  // from the tail of the event queue

  events.sort((a, b) => {
    if (a.position !== b.position) {
      return b.position - a.position;
    }

    return !a.isEnter && b.isEnter ? 1 : a.isEnter && !b.isEnter ? -1 : 0;
  });

  /* process event queue, adding missing grid lines to ordinate set */

  while (last(events)) {
    const { isEnter, position } = last(events)!;

    if (isEnter && !isOdd(ordinates.length)) {
      /* insert the missing exit before this enter */

      ordinates.push(position - epsilon);
    }

    if (!isEnter && isOdd(ordinates.length)) {
      /* insert the missing enter before this exit */

      ordinates.push(position - epsilon);
    }

    /* insert this position */

    ordinates.push(position);

    /* remove this, and any equivalent, events */

    while (
      last(events) &&
      last(events)?.position === position &&
      last(events)?.isEnter === isEnter
    ) {
      events.pop();
    }
  }

  return ordinates;
};

export const stripGrid = (
  group: BlocksNode,
  setLayout: (
    u: BlocksNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => void
) => {
  if (!group.children) {
    return;
  }

  const epsilon = 0.1;
  const leftEdge = (u: BlocksNode) => u.layoutData!.col;
  const topEdge = (u: BlocksNode) => u.layoutData!.row;
  const rightEdge = (u: BlocksNode) =>
    u.layoutData!.col + u.layoutData!.colSpan - epsilon;
  const bottomEdge = (u: BlocksNode) =>
    u.layoutData!.row + u.layoutData!.rowSpan - epsilon;

  /* get unique edge ordinates */

  const cols = buildOrdinates(group.children, leftEdge, rightEdge);
  const rows = buildOrdinates(group.children, topEdge, bottomEdge);

  for (const child of group.children) {
    /* indices within the unique ordinates are the new col and row */

    const col = sortedIndexOf(cols, leftEdge(child));
    const row = sortedIndexOf(rows, topEdge(child));
    const colSpan = sortedIndexOf(cols, rightEdge(child)) - col;
    const rowSpan = sortedIndexOf(rows, bottomEdge(child)) - row;

    if (
      col !== child.layoutData!.col ||
      row !== child.layoutData!.row ||
      colSpan !== child.layoutData!.colSpan ||
      rowSpan !== child.layoutData!.rowSpan
    ) {
      setLayout(child, col, row, colSpan, rowSpan);
    }
  }
};
